/* eslint-disable max-lines-per-function */
import React from "react";
import { useQuery } from "@apollo/client";
import { InputAdornment, TextField, useMediaQuery, Grid } from "@mui/material";
import { Search } from "@mui/icons-material";
import { toLower } from "lodash";
import { PropertiesPage, PropertiesPage_propertiesPage as PropertySummary } from "types/gql-types";
import { LoadingSpinner } from "components/graphql-views";
import { Button, ContainerCardLayout } from "components/haven-design";
import theme from "components/theme";
import propertyQuery from "./query/properties.gql";
import PropertyCard from "./property";
import FiltersModal, { Filters } from "./filters-modal";
import { FilterChips } from "./filter-chips";

export const initialPropertyFilters = {
  searchString: "",
  selectedCities: [],
  selectedStates: [],
  unitSizes: [],
  income: null,
  incomeFrequency: "month",
} as Filters;
type Props = {
  path?: string;
}

const filterByUnit = (results:PropertySummary[], filters:Filters):PropertySummary[] => {
  if (filters.unitSizes === null || !filters.unitSizes.length) return results;

  return results.filter(
    ({ unitSizes }) => unitSizes.some(unit => filters.unitSizes.includes(unit)),
  );
};

const filterByLocation =
  (results:PropertySummary[], filters:Filters):PropertySummary[] => {
    const fullStates = filters.selectedStates.filter(
      value =>
        !results.some(
          ({ city, state }) => value === state && filters.selectedCities.includes(city),
        ),
    );
    return results
      .filter(property => {
        if (!filters.selectedCities.length && !filters.selectedStates.length) return true;
        return fullStates.includes(property.state) ||
          filters.selectedCities.includes(property.city);
      });
  };

const containsSearchString = ({ city, name, orgName }: PropertySummary, searchString: string) =>
  [city, name, orgName].map(toLower).some((input) => input.includes(searchString.toLowerCase()));
const filterBySearchString = (input: PropertySummary[], { searchString }: Filters) => {
  const searchFilter = searchString.trim().toLowerCase();
  if (searchFilter.length === 0) return input;
  return input.filter((result) => containsSearchString(result, searchFilter));
};
const filtersToApply = [filterBySearchString, filterByUnit, filterByLocation];

const PropertiesContent: React.FC<{properties: PropertySummary[], refetch: () => void}> =
({ properties: results, refetch }) => {

  const [isOpen, setIsOpen] = React.useState(false);
  const isSmall = useMediaQuery(theme.breakpoints.down("xs"));
  const [filters, setFilters] = React.useState(initialPropertyFilters);

  const filteredResults = filtersToApply.reduce((acc, filter) => filter(acc, filters), results);

  const extendedResults = results
    .filter(result => !filteredResults.includes(result))
    .filter(result => (filters
      .selectedStates.length < 0 || filters
      .selectedStates.includes(result.state)));
  return (<>
    <Grid container justifyContent="flex-start" alignItems="center" size={{ xs: 12 }} spacing={2}>
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <TextField fullWidth margin="dense" InputProps={{
          endAdornment: <InputAdornment position="end"><Search /></InputAdornment>,
        }}
        label="Search by Organization or Property Name"
        value={filters.searchString}
        onChange={(event) => {
          const newValue = event.currentTarget.value;
          setFilters((oldFilters) => ({ ...oldFilters, searchString: newValue }));
        }}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 2 }}>
        <Button size="L" onClick={() => setIsOpen(true)} fullWidth={isSmall} color="primary">Filters</Button>
      </Grid>
    </Grid>
    <Grid size={{ xs: 12 }}>
      <p>Showing {filteredResults.length} results</p>
      <FilterChips filters={filters} onChange={(values) => setFilters(values)} />
    </Grid>
    <Grid container size={{ xs: 12 }} spacing={1}>
      {filteredResults.map((property) => (
        <PropertyCard
          key={property.id}
          property={property}
          onApplicationRemoved={() => refetch() }
        />))}
    </Grid>
    {extendedResults.length > 0 &&
      <><Grid size={{ xs: 12 }}>
        <p>Other results in the selected state(s):</p>
      </Grid>
      <Grid container size={{ xs: 12 }} spacing={1}>
        {extendedResults.map((property) => (
          <PropertyCard
            key={property.id}
            property={property}
            onApplicationRemoved={() => refetch() }
          />))}
      </Grid>
      </>}
    <FiltersModal
      properties={results}
      isOpen={isOpen}
      onRequestClose={(values) => {
        setFilters(values);
        setIsOpen(false);
      }}
      currentValues={filters}
    />
  </>);
};

const Properties: React.FC<Props> = () => {
  const { data, error, loading, refetch } =
    useQuery<PropertiesPage>(propertyQuery, { fetchPolicy: "network-only" });
  return (
    <ContainerCardLayout title="Affordable Housing">
      <Grid style={{ paddingTop: 8 }} container direction="column" spacing={2}>
        {loading && <LoadingSpinner />}
        {!loading && error && <div>Error loading properties</div>}

        {data && <PropertiesContent properties={data.propertiesPage} refetch={refetch} />}
      </Grid>
    </ContainerCardLayout>
  );
};

export default Properties;
